import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import React, { useEffect, useState } from "react";
import {
	type FavoriteWorkshops,
	getAddWorkshopToFavorites,
	getFavoriteWorkshops,
	type Author,
} from "../../api/api";
import { formatDate } from "../../utils/dateAndTime";
import { HeartIcon } from "../icons/heart";
import { HeartDarkIcon } from "../icons/heartFull";
import { useQuery } from "@tanstack/react-query";
import { LoaderSpinner } from "../ui/LoaderSpinner";
import { cn } from "../utils/utils";
import { useAuth } from "@/context/AuthContext";
import { getCategoryBadgeStyle, getCategoryGradient } from "@/utils/colorsWorkshop";

type CategoryV1 = string;
type CategoryV2 = { id: number; name: string };
type CategoriesArray = Array<CategoryV1> | Array<CategoryV2>;
const filterCategories = (categories: CategoriesArray): CategoriesArray => {
	if (categories.length > 0 && typeof categories[0] === "string") {
		return (categories as Array<string>).filter(
			(category) => category !== "NULL" && category !== ""
		);
	}
	return (categories as Array<CategoryV2>).filter(
		(category) => category.name !== "NULL" && category.name !== ""
	);
};

const getFavoriteStatus = (
	slug: string | undefined,
	favoriteWorkshops: FavoriteWorkshops | null | undefined,
	isAuthenticated: boolean,
): "DISABLED" | "IS_FAVORITE" | "NOT_FAVORITE" => {
	if (!isAuthenticated) {
		return "DISABLED";
	}
	if (slug === undefined) {
		return "DISABLED";
	}
	if (favoriteWorkshops === undefined || favoriteWorkshops === null) {
		return "DISABLED";
	}
	if (favoriteWorkshops.favorites.some((workshop) => workshop.slug === slug)) {
		return "IS_FAVORITE";
	}
	return "NOT_FAVORITE";
};

// Mapowanie kategorii na gradienty
const getCategoryGradientFromCategories = (categories: CategoriesArray): string => {
	if (!categories || categories.length === 0) {
		return getCategoryGradient('');
	}

	const firstCategory = categories[0];
	if (!firstCategory) {
		return getCategoryGradient('');
	}
	
	const categoryName = typeof firstCategory === 'string' ? firstCategory : firstCategory.name;
	return getCategoryGradient(categoryName);
};


export interface CoverCardV1Props {
	coverId: number;
	title: string;
	authors: Array<Author>;
	coverDate: number;
	categories: Array<string> | Array<{ id: number; name: string }>; // V1
	slug?: string;
}
const CoverCardNewVisual: React.FC<CoverCardV1Props> = React.forwardRef<HTMLDivElement, CoverCardV1Props>(
	(props, ref) => {
		const { authors, title, coverDate, categories, coverId, slug } = props;
		const { isAuthenticated } = useAuth();

		const {
			isFetching: isFeatchingFavoriteWorkshops,
			data: favoriteWorkshops,
			refetch: refetchFavoriteWorkshops,
		} = useQuery({
			queryKey: ["favorite-workshops"],
			queryFn: () => getFavoriteWorkshops(),
			staleTime: 1000 * 60 * 5,
			enabled: isAuthenticated,
		});

		// const favoriteStatus = getFavoriteStatus(slug, favoriteWorkshops);

		const {
			isFetching: isFetchingAddToFavorites,
			refetch: refetchAddToFavorites,
		} = useQuery({
			queryKey: ["add_product_to_favorites", slug],
			queryFn: () => getAddWorkshopToFavorites(slug ?? ""),
			enabled: false,
		});

		const [loading, setLoading] = useState(false);
		const [favoriteStatus, setFavoriteStatus] = useState<
			"DISABLED" | "IS_FAVORITE" | "NOT_FAVORITE"
		>(getFavoriteStatus(slug, favoriteWorkshops, isAuthenticated));

		useEffect(() => {
			if (!isFetchingAddToFavorites && !isFeatchingFavoriteWorkshops) {
				setLoading(false);
			}
		}, [isFetchingAddToFavorites, isFeatchingFavoriteWorkshops]);

		useEffect(() => {
			setFavoriteStatus(getFavoriteStatus(slug, favoriteWorkshops, isAuthenticated));
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [favoriteWorkshops]);

		const handleAddToFavorites = async (
			event: React.MouseEvent<HTMLButtonElement>
		): Promise<void> => {
			event.stopPropagation();
			event.preventDefault();
			setLoading(true);
			await refetchAddToFavorites();
			await refetchFavoriteWorkshops();
		};

		const gradientBackground = getCategoryGradientFromCategories(categories);

		return (
			<Card
				key={`CoverCardMVP--Card--${coverId}`}
				ref={ref}
				className="relative z-0 min-h-[176px] flex flex-col justify-between rounded-xl p-3"
				style={{
					background: gradientBackground,
				}}
			>
				<div
					className={cn(
						"flex justify-between",
						favoriteStatus === "DISABLED" ? "w-full" : "w-[calc(100%-40px)]"
					)}
				>
					<div className="flex gap-1">
						{categories &&
													filterCategories(categories)?.map((category) => {
							const categoryName = typeof category === "string" ? category : category.name;
							
							return (
								<Badge
									key={`${categoryName}${coverId}`}
									className="text-xs font-medium"
									style={getCategoryBadgeStyle(categoryName)}
								>
									{categoryName}
								</Badge>
							);
						})}
					</div>
					<Badge key={`coverDate` + coverId} variant="semitransparent">
						{formatDate(coverDate * 1000)}
					</Badge>
					{favoriteStatus !== "DISABLED" && (
						<div className="absolute right-0 top-0">
							{loading ? (
								<LoaderSpinner className="p-3" size={6} />
							) : (
								<button
									className="p-3"
									onClick={(event) => handleAddToFavorites(event)}
								>
									{favoriteStatus === "IS_FAVORITE" ? (
										<HeartDarkIcon color="white" />
									) : (
										<HeartIcon color="white" />
									)}
								</button>
							)}
						</div>
					)}
				</div>
				<h3
					className={cn(
						"text-white text-md line-clamp-3 z-30",
						authors.length > 1 ? "w-3/5" : "w-[70%]"
					)}
				>
					{title}
				</h3>
				<div className="z-20">
					{authors?.map((author) => (
						<Badge
							key={coverId + author.display_name}
							className="mr-2"
							id={`${author.WP_user_ID}`}
							variant="semitransparent"
						>
							{author.display_name}
						</Badge>
					))}
				</div>
				{authors?.[0] && (
					<LazyLoadImage
						alt={authors[0].display_name}
						delayTime={1}
						effect="blur"
						src={authors[0].profile_picture}
						threshold={300}
						wrapperProps={{
							style: {
								position: "absolute",
								bottom: "0",
								right: "0",
								width: "1/4",
								maxHeight: "135px",
								maxWidth: "135px",
							},
						}}
					/>
				)}
				{authors?.[1] && (
					<LazyLoadImage
						visibleByDefault
						alt={authors[1].display_name}
						effect="blur"
						src={authors[1].profile_picture}
						threshold={300}
						wrapperProps={{
							style: {
								position: "absolute",
								bottom: "0",
								right: "2.5rem",
								width: "1/4",
								maxHeight: "135px",
								maxWidth: "135px",
							},
						}}
					/>
				)}
			</Card>
		);
	}
);

export default CoverCardNewVisual;
